import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Layout from '../../Layout/Layout';
import { AppButtonLink } from '../../components/AppButton/AppButton';
import './About.css';

const VALUES = [
    {
        label: 'Who we are',
        icon: 'mdi:account-group-outline',
        title: 'Builders at the intersection of tech and trading',
        description: 'We are engineers, product designers, and traders united by one goal: make crypto automation accessible, reliable, and transparent for everyone.',
    },
    {
        label: 'Our mission',
        icon: 'mdi:target',
        title: 'Turn chaos into opportunity',
        description: 'Trading should not be a game of chance. NeuraTrader delivers disciplined automation so you can engage the market with rules, not emotion.',
    },
    {
        label: 'Why we started',
        icon: 'mdi:lightbulb-on-outline',
        title: 'Born from real trading pain',
        description: 'We have watched markets move 24/7 while humans cannot. NeuraTrader was built to be the tireless partner that executes your strategy without panic or fatigue.',
    },
    {
        label: 'Our commitment',
        icon: 'mdi:handshake-outline',
        title: 'Your trust comes first',
        description: 'We ship carefully, test rigorously, and communicate plan limits clearly. Every feature is designed to earn your confidence — not just your subscription.',
    },
];

const PRODUCTS = [
    {
        icon: 'mdi:brain',
        title: 'Algo Bot',
        description: 'Wire monitors, conditions, and orders into full buy/sell strategies.',
    },
    {
        icon: 'mdi:clock-outline',
        title: 'Scheduled Bot',
        description: 'Run DCA and time-based strategies on a schedule you control.',
    },
    {
        icon: 'mdi:grid',
        title: 'Grid Bot',
        description: 'Capture range-bound price swings with layered buy and sell orders.',
    },
];

const STATS = [
    { value: '24/7', label: 'Market coverage' },
    { value: '3', label: 'Bot types' },
    { value: '100%', label: 'Your strategy rules' },
];

function ValueCard({ item }) {
    return (
        <article className="about-value-card">
            <div className="about-value-top">
                <span className="about-value-icon" aria-hidden="true">
                    <Icon icon={item.icon} width={22} height={22} />
                </span>
                <div>
                    <p className="about-value-label">{item.label}</p>
                    <h3 className="about-value-title">{item.title}</h3>
                </div>
            </div>
            <p className="about-value-text">{item.description}</p>
        </article>
    );
}

const Company = () => {
    return (
        <Layout>
            <div className="about-page">
                <header className="about-hero">
                    <div className="about-hero-inner">
                        <span className="about-eyebrow">About NeuraTrader</span>
                        <h1 className="about-hero-title">The team behind smarter trading</h1>
                        <p className="about-hero-subtitle">
                            We build professional automation tools for crypto traders —
                            so you can focus on strategy while NeuraTrader handles execution.
                        </p>
                        <div className="about-hero-actions">
                            <AppButtonLink to="/register" variant="primary" size="md">
                                Get started
                            </AppButtonLink>
                            <AppButtonLink to="/contact" variant="secondary" size="md">
                                Contact us
                            </AppButtonLink>
                        </div>
                    </div>
                </header>

                <section className="about-section about-section--tight-bottom">
                    <div
                        className="about-banner"
                        style={{ backgroundImage: 'url(/images/trade_main.avif)' }}
                    >
                        <div className="about-banner-overlay">
                            <p className="about-banner-quote">
                                &ldquo;We believe the best trading tools remove friction, add clarity,
                                and never trade on your behalf without your rules.&rdquo;
                            </p>
                        </div>
                    </div>

                    <div className="about-section-header">
                        <h2 className="about-section-title">What drives us</h2>
                        <p className="about-section-text">
                            Four principles that guide every product decision we make.
                        </p>
                    </div>

                    <div className="about-values-grid">
                        {VALUES.map((item) => (
                            <ValueCard key={item.label} item={item} />
                        ))}
                    </div>
                </section>

                <section className="about-section">
                    <div className="about-section-header">
                        <h2 className="about-section-title">What we build</h2>
                        <p className="about-section-text">
                            NeuraTrader is a complete bot platform — not a single trick pony.
                        </p>
                    </div>

                    <div className="about-products">
                        {PRODUCTS.map((product) => (
                            <article key={product.title} className="about-product-card">
                                <span className="about-product-icon" aria-hidden="true">
                                    <Icon icon={product.icon} width={20} height={20} />
                                </span>
                                <h3 className="about-product-title">{product.title}</h3>
                                <p className="about-product-text">{product.description}</p>
                            </article>
                        ))}
                    </div>

                    <div className="about-stats-row">
                        {STATS.map((stat) => (
                            <div key={stat.label} className="about-stat-card">
                                <span className="about-stat-value">{stat.value}</span>
                                <span className="about-stat-label">{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="about-cta-wrap">
                    <div className="about-cta-card">
                        <h2 className="about-cta-title">Join our journey</h2>
                        <p className="about-cta-text">
                            Be part of a community shaping the future of automated trading.
                            Explore our guides, pick a plan, and launch your first bot today.
                        </p>
                        <AppButtonLink to="/register" variant="secondary" size="lg">
                            Create free account
                        </AppButtonLink>
                        <p className="about-cta-note">
                            New to bots? Start with our{' '}
                            <Link to="/learn" className="about-cta-link">setup guides</Link>.
                        </p>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default Company;